/**
 * Who owns a per-group resource (product owner, 2026-10-28): a group somebody made, by its id, or
 * one of the four controls, by its kind. The controls left the groups table — they live in code and
 * keep their members, executions and measurements by kind — so everything that used to be keyed by
 * a group id is keyed by one of these instead, and a control's page reads what a group's does.
 *
 * Exactly one of the two is set, here and in every payload (`group_id` / `control_kind` upstream,
 * `groupId` / `controlKind` on this side). The key is what caches and lists are keyed by: `g:12`
 * or `c:gestures`, never a bare number that could be mistaken for a group's.
 */
import type { ControlKind, GroupOwner } from '../model.js';

/** The product owner's order, which is also the order of the cards. */
export const CONTROL_KINDS: readonly ControlKind[] = Object.freeze([
  'gestures',
  'gesture_alerts',
  'semantics',
  'coherence',
]);

export function isControlKind(value: unknown): value is ControlKind {
  return typeof value === 'string' && (CONTROL_KINDS as readonly string[]).includes(value);
}

export function groupOwner(groupId: number): GroupOwner {
  return { groupId };
}

export function controlOwner(controlKind: ControlKind): GroupOwner {
  return { controlKind };
}

export function isControlOwner(owner: GroupOwner): owner is { controlKind: ControlKind } {
  return 'controlKind' in owner;
}

/** The group's id, or null for a control. */
export function ownerGroupId(owner: GroupOwner | null | undefined): number | null {
  return owner && !isControlOwner(owner) ? owner.groupId : null;
}

/** The control's kind, or null for a group. */
export function ownerControlKind(owner: GroupOwner | null | undefined): ControlKind | null {
  return owner && isControlOwner(owner) ? owner.controlKind : null;
}

/**
 * The owner a payload names, or null when it names none — a run of a video's own, a row from an
 * older upstream. A kind this build does not know is no owner either: nothing here guesses.
 */
export function ownerOf(
  groupId: number | string | null | undefined,
  controlKind: string | null | undefined,
): GroupOwner | null {
  if (isControlKind(controlKind)) return { controlKind };

  const id = typeof groupId === 'string' ? Number(groupId) : groupId;
  if (typeof id === 'number' && Number.isSafeInteger(id) && id > 0) return { groupId: id };

  return null;
}

/** `g:<id>` or `c:<kind>`: what caches, lists and selections are keyed by. */
export function ownerKey(owner: GroupOwner): string {
  return isControlOwner(owner) ? `c:${owner.controlKind}` : `g:${owner.groupId}`;
}

export function sameOwner(
  a: GroupOwner | null | undefined,
  b: GroupOwner | null | undefined,
): boolean {
  if (!a || !b) return a === b;

  return ownerKey(a) === ownerKey(b);
}
